import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { ArrowSquareOut } from 'phosphor-react';

export type PriceOptionSource = 'price_list' | 'kame' | 'oc';

export interface PriceOption {
  source: PriceOptionSource;
  /** null = source exists but price is unavailable (shows —, not clickable) */
  price: number | null;
}

interface PriceOptionsPopoverProps {
  options: PriceOption[];
  /** The currently active source, if any */
  activeSource?: string | null;
  /** Ref to the anchor element (price input) used to position the popover */
  anchorRef: React.RefObject<HTMLElement | null>;
  onSelect: (price: number, source: PriceOptionSource) => void;
  /** If set and a price_list row has no price, show an "Abrir" link to the list */
  priceListId?: number | null;
}

const SOURCE_META: Record<PriceOptionSource, { dot: string; label: string }> = {
  price_list: { dot: 'bg-green-300/60',  label: 'Lista' },
  kame:       { dot: 'bg-yellow-300/60', label: 'Kame' },
  oc:         { dot: 'bg-blue-300/60',   label: 'OC' },
};

export function PriceOptionsPopover({ options, activeSource, anchorRef, onSelect, priceListId }: PriceOptionsPopoverProps) {
  const [rect, setRect] = React.useState<DOMRect | null>(null);

  React.useLayoutEffect(() => {
    if (anchorRef.current) {
      setRect(anchorRef.current.getBoundingClientRect());
    }
  }, [anchorRef]);

  if (options.length === 0 || !rect) return null;

  const popover = (
    <div
      role="listbox"
      aria-label="Opciones de precio"
      style={{
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        minWidth: rect.width,
        zIndex: 9999,
      }}
      className="min-w-[160px] rounded-md border border-border bg-background shadow-lg py-1 text-sm"
      onMouseDown={(e) => e.preventDefault()}
    >
      {options.map(({ source, price }) => {
        const meta = SOURCE_META[source];
        const isActive = activeSource === source;
        const hasPrice = price !== null;

        if (!hasPrice) {
          if (source === 'price_list' && priceListId) {
            return (
              <a
                key={source}
                href={`/configuracion/listas-de-precios/${priceListId}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted transition-colors text-text-subtle"
                onMouseDown={(e) => { e.preventDefault(); window.open(`/configuracion/listas-de-precios/${priceListId}`, '_blank'); }}
              >
                <span className={`h-2 w-2 rounded-full flex-shrink-0 ${meta.dot}`} />
                <span className="flex-1 whitespace-nowrap">{meta.label}</span>
                <span className="flex items-center gap-1 text-xs font-medium text-primary">
                  Abrir <ArrowSquareOut size={12} weight="bold" />
                </span>
              </a>
            );
          }
          return (
            <div
              key={source}
              className="flex items-center gap-2 px-3 py-1.5 opacity-40 cursor-default select-none"
            >
              <span className={`h-2 w-2 rounded-full flex-shrink-0 ${meta.dot}`} />
              <span className="text-text-subtle flex-1 whitespace-nowrap">{meta.label}</span>
              <span className="font-medium tabular-nums">—</span>
            </div>
          );
        }

        return (
          <button
            key={source}
            role="option"
            aria-selected={isActive}
            type="button"
            onClick={() => onSelect(price, source)}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-muted transition-colors ${
              isActive ? 'bg-muted/60' : ''
            }`}
          >
            <span className={`h-2 w-2 rounded-full flex-shrink-0 ${meta.dot}`} />
            <span className="text-text-subtle flex-1 whitespace-nowrap">{meta.label}</span>
            <span className="font-medium tabular-nums">
              ${price.toLocaleString('es-CL')}
            </span>
            {isActive && (
              <span className="text-text-subtle text-xs ml-1">✓</span>
            )}
          </button>
        );
      })}
    </div>
  );

  return ReactDOM.createPortal(popover, document.body);
}
